import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";
import { CTASection } from "@/components/CTASection";
import { proTipsArticles } from "@/data/proTipsData";
import { CalendarDays, ArrowRight } from "lucide-react";

// Import hero images for thumbnails
import masonryHero from "@/assets/brick-work-masonry-stairs-wayne-nj-hero.webp";
import patioHero from "@/assets/patio-pavers-mountain-lakes-morris-county-nj-2.webp";
import stoopHero from "@/assets/steps-stoops-masonry-wayne-nj.webp";
import paverCostHero from "@/assets/mp-hero-paver-patio-installation-paramus-nj-061126.webp";

const imageMap: Record<string, string> = {
  "brick-work-masonry-stairs-wayne-nj-hero.webp": masonryHero,
  "paver-patio-north-new-jersey.webp": patioHero,
  "steps-stoops-masonry-wayne-nj.webp": stoopHero,
  "mp-hero-paver-patio-installation-paramus-nj-061126.webp": paverCostHero,
};

export default function ProTips() {
  return (
    <>
      <Helmet>
        <title>Masonry & Paving Pro Tips North Jersey | Masonry Pro</title>
        <meta name="description" content="Expert masonry and paving tips from Masonry Pro Construction. Learn about paver patios, retaining walls, and concrete from North Jersey's trusted contractor." />
        <meta property="og:description" content="Expert masonry and paving tips from Masonry Pro Construction. Learn about paver patios, retaining walls, and concrete from North Jersey's trusted contractor." />
        <meta property="og:title" content="Masonry & Paving Pro Tips North Jersey | Masonry Pro" />
        
        <meta property="og:image" content="https://masonry-pro.com/og/services.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
      </Helmet>
      <Header />
      <Breadcrumb />

      <main className="bg-background">
        {/* Hero */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4 text-center max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Pro <span className="text-gradient-gold">Tips</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Insights, advice, and expert knowledge from over 35 years in the masonry and paving trade, straight from the Masonry Pro team.
            </p>
          </div>
        </section>

        {/* Article Grid */}
        <section className="pb-24">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {proTipsArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/pro-tips/${article.slug}`}
                  className="group rounded-xl overflow-hidden border border-border bg-card hover:border-secondary/50 transition-all duration-300 hover:shadow-lg"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={imageMap[article.heroImage]}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                      <CalendarDays className="h-4 w-4" />
                      {new Date(article.date).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </div>
                    <h2 className="text-xl font-bold mb-3 group-hover:text-secondary transition-colors">
                      {article.title}
                    </h2>
                    <p className="text-muted-foreground text-sm mb-4">
                      {article.excerpt}
                    </p>
                    <span className="inline-flex items-center gap-1 text-secondary font-semibold text-sm">
                      Read More <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <CTASection />
      </main>
      <Footer />
    </>
  );
}
